/**
 * Preview of the lead figures while typing. The server recomputes and stores
 * the real numbers on save (§6) — this only keeps the form responsive.
 */
import Decimal from 'decimal.js';

Decimal.set({ rounding: Decimal.ROUND_HALF_UP, precision: 28 });

export interface PreviewInput {
  amountVatEx: string;
  vatApplicable: boolean;
  vatRate: number;
  taxClass: 'GOODS' | 'SERVICES' | 'MIXED';
  goodsAmount: string;
  servicesAmount: string;
  ewtGoods: number;
  ewtServices: number;
  isGovernment: boolean;
  wvatGov: number;
  projectCost: string;
  commissions: { amount: string }[];
  ewtCommission: number;
  stageProbability: number;
}

export interface Preview {
  amountVatEx: number;
  vat: number;
  totalContractPrice: number;
  ewtAmount: number;
  wvatAmount: number;
  expectedNetCollection: number;
  grossProfit: number;
  grossMarginPct: number;
  totalCommission: number;
  commissionEwt: number;
  netProfit: number;
  netMarginPct: number;
  weightedAmount: number;
}

const d = (value: string | number): Decimal => {
  const parsed = new Decimal(String(value === '' || value === null ? 0 : value));
  return parsed.isFinite() ? parsed : new Decimal(0);
};
const round2 = (value: Decimal): number => value.toDecimalPlaces(2).toNumber();

export function previewLead(input: PreviewInput): Preview {
  const amount = d(input.amountVatEx);
  const vatRate = input.vatApplicable ? d(input.vatRate) : new Decimal(0);
  const vat = amount.times(vatRate);
  const total = amount.plus(vat);

  const ewt =
    input.taxClass === 'GOODS'
      ? amount.times(d(input.ewtGoods))
      : input.taxClass === 'SERVICES'
        ? amount.times(d(input.ewtServices))
        : d(input.goodsAmount).times(d(input.ewtGoods)).plus(
            d(input.servicesAmount).times(d(input.ewtServices)),
          );

  const wvat = input.isGovernment ? amount.times(d(input.wvatGov)) : new Decimal(0);
  const cost = d(input.projectCost);
  const grossProfit = amount.minus(cost);
  const commissionTotal = input.commissions.reduce((sum, row) => sum.plus(d(row.amount)), new Decimal(0));
  const netProfit = grossProfit.minus(commissionTotal);

  return {
    amountVatEx: round2(amount),
    vat: round2(vat),
    totalContractPrice: round2(total),
    ewtAmount: round2(ewt),
    wvatAmount: round2(wvat),
    expectedNetCollection: round2(total.minus(ewt).minus(wvat)),
    grossProfit: round2(grossProfit),
    grossMarginPct: amount.isZero() ? 0 : grossProfit.dividedBy(amount).toNumber(),
    totalCommission: round2(commissionTotal),
    commissionEwt: round2(commissionTotal.times(d(input.ewtCommission))),
    netProfit: round2(netProfit),
    netMarginPct: amount.isZero() ? 0 : netProfit.dividedBy(amount).toNumber(),
    weightedAmount: round2(amount.times(d(input.stageProbability))),
  };
}

export function sumItems(items: { amountVatEx: string }[]): number {
  return items.reduce((sum, item) => sum.plus(d(item.amountVatEx)), new Decimal(0)).toDecimalPlaces(2).toNumber();
}
